import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Col, Progress, Row, Spin, Statistic, Table, Tag, Typography, message } from 'antd'
import { get } from '../../lib/request'
import { hasAnyRole, useAuthStore } from '../../stores/auth'
import type { AdminRole } from '../../stores/auth'
import {
  DashboardCharts,
  DashboardStats,
  LEAD_STATUS_LABELS,
  SOURCE_MODE_LABELS,
  errMsg,
} from '../../lib/api'
import AnalyticsCard from '../../components/AnalyticsCard'

const { Paragraph, Text, Title } = Typography

const PIE_COLORS = ['#2557A7', '#C9A84C', '#16A085', '#F39C12', '#8E44AD']
const FUNNEL_ORDER: Array<keyof typeof LEAD_STATUS_LABELS> = [
  'pending', 'contacted', 'toured', 'dealt', 'verified', 'rewarded',
]

/**
 * 工作台（full_feature_v3）
 * 指标卡（dashboard/stats）+ 三图（趋势/来源饼图/转化漏斗，无图表库用条形与 Progress 实现）
 * + 转介之星排行 + 四类待办（含保护期即将到期预警 7 天）
 */
const DashboardPage: React.FC = () => {
  const navigate = useNavigate()
  const role = useAuthStore((s) => s.user?.role)
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [charts, setCharts] = useState<DashboardCharts | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      get<DashboardStats>('/admin/dashboard/stats'),
      get<DashboardCharts>('/admin/dashboard/charts'),
    ])
      .then(([s, c]) => {
        setStats(s)
        setCharts(c)
      })
      .catch((err) => message.warning(errMsg(err, '统计数据加载失败（后端可能未启动）')))
      .finally(() => setLoading(false))
  }, [])

  // 角色数组与 App.tsx RoleRoute 定义保持一致（逐字复制）
  const cards: Array<{ label: string; value: number | undefined; color?: string; href: string; roles: AdminRole[] }> = [
    { label: '累计线索', value: stats?.cards.totalLeads, href: '/leads', roles: ['super_admin', 'admin', 'operator', 'leasing_director', 'leasing_manager', 'rental_consultant'] },
    { label: '本月新增', value: stats?.cards.monthlyLeads, color: '#2557A7', href: '/leads', roles: ['super_admin', 'admin', 'operator', 'leasing_director', 'leasing_manager', 'rental_consultant'] },
    { label: '累计成交', value: stats?.cards.dealtLeads, color: '#27AE60', href: '/rewards', roles: ['super_admin', 'admin', 'finance', 'finance_manager'] },
    { label: '活跃推荐人', value: stats?.cards.activeReferrers, href: '/referrers', roles: ['super_admin', 'admin', 'operator', 'finance', 'finance_manager', 'leasing_director', 'leasing_manager', 'rental_consultant'] },
  ]

  const todos = [
    { label: '待分配（公海）', value: stats?.todos.pendingAssignment, color: '#E74C3C', href: '/leads' },
    { label: '待新客校验', value: stats?.todos.pendingVerification, color: '#F39C12', href: '/review-center' },
    { label: '待发放奖励', value: stats?.todos.pendingRewards, color: '#C9A84C', href: '/rewards' },
    { label: '待审身份申请', value: stats?.todos.pendingApplications, color: '#2557A7', href: '/review-center' },
    { label: '保护期 7 天内到期', value: stats?.expiringProtections, color: '#8E44AD', href: '/leads' },
  ]

  const trendMax = Math.max(1, ...(charts?.trend ?? []).map((t) => t.count))
  const pieTotal = Math.max(1, (charts?.sourcePie ?? []).reduce((s, p) => s + p.count, 0))

  return (
    <Spin spinning={loading}>
      <Row gutter={[16, 16]}>
        {cards.map((c) => {
          const allowed = hasAnyRole(role, ...c.roles)
          return (
            <Col key={c.label} xs={12} md={6}>
              {allowed ? (
                <Card
                  hoverable
                  style={{ borderRadius: 12 }}
                  styles={{ body: { padding: '16px 18px' } }}
                  onClick={() => navigate(c.href)}
                >
                  <Statistic
                    title={c.label}
                    value={c.value ?? '—'}
                    valueStyle={{ color: c.color ?? '#2C3E50', fontWeight: 700 }}
                  />
                </Card>
              ) : (
                <Card style={{ borderRadius: 12 }} styles={{ body: { padding: '16px 18px' } }}>
                  <Statistic
                    title={c.label}
                    value={c.value ?? '—'}
                    valueStyle={{ color: c.color ?? '#2C3E50', fontWeight: 700 }}
                  />
                </Card>
              )}
            </Col>
          )
        })}
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {todos.map((t) => (
          <Col key={t.label} xs={12} md={8} lg={4} style={{ flex: '1 1 0' }}>
            <Card
              hoverable
              style={{ borderRadius: 12 }}
              styles={{ body: { padding: '14px 16px', minHeight: 88 } }}
              onClick={() => { window.location.href = t.href }}
            >
              <Statistic
                title={<span style={{ fontSize: 13 }}>{t.label}</span>}
                value={t.value ?? '—'}
                valueStyle={{ color: t.color, fontWeight: 700, fontSize: 24 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={10}>
          <Card title="线索趋势（近 30 天）" style={{ borderRadius: 12 }}>
            {(charts?.trend ?? []).length === 0 ? (
              <Text type="secondary">暂无数据</Text>
            ) : (
              <div>
                {(charts?.trend ?? []).slice(-14).map((t) => (
                  <div key={t.date} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ width: 86, fontSize: 12, color: '#7F8C8D', flexShrink: 0 }}>{t.date}</span>
                    <div style={{ flex: 1, background: '#F0F2F5', borderRadius: 4, height: 14, overflow: 'hidden' }}>
                      <div style={{ width: `${(t.count / trendMax) * 100}%`, height: '100%', background: '#2557A7' }} />
                    </div>
                    <span style={{ width: 32, fontSize: 12, fontWeight: 600 }}>{t.count}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={7}>
          <Card title="来源分布" style={{ borderRadius: 12 }}>
            {(charts?.sourcePie ?? []).length === 0 ? (
              <Text type="secondary">暂无数据</Text>
            ) : (
              <div>
                {(charts?.sourcePie ?? []).map((p, i) => (
                  <div key={p.sourceMode} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span
                      style={{
                        width: 10, height: 10, borderRadius: 2, flexShrink: 0,
                        background: PIE_COLORS[i % PIE_COLORS.length],
                      }}
                    />
                    <span style={{ flex: 1, fontSize: 13 }}>{SOURCE_MODE_LABELS[p.sourceMode] ?? p.sourceMode}</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{p.count}</span>
                    <Progress
                      percent={Math.round((p.count / pieTotal) * 100)}
                      showInfo={false}
                      strokeColor={PIE_COLORS[i % PIE_COLORS.length]}
                      style={{ width: 80, marginBottom: 0 }}
                    />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={7}>
          <Card title="转化漏斗" style={{ borderRadius: 12 }}>
            {(charts?.funnel ?? []).length === 0 ? (
              <Text type="secondary">暂无数据</Text>
            ) : (
              <div>
                {FUNNEL_ORDER.map((st) => {
                  const f = (charts?.funnel ?? []).find((x) => x.status === st)
                  const count = f?.count ?? 0
                  const base = (charts?.funnel ?? [])[0]?.count ?? 1
                  return (
                    <div key={st} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span>{LEAD_STATUS_LABELS[st].label}</span>
                        <strong>{count}</strong>
                      </div>
                      <Progress
                        percent={Math.round((count / Math.max(1, base)) * 100)}
                        showInfo={false}
                        strokeColor={LEAD_STATUS_LABELS[st].color === 'warning' ? '#F39C12' : undefined}
                      />
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}><AnalyticsCard /></Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="推荐之星排行（按线索数 Top 10）" style={{ borderRadius: 12 }}>
            <Table
              rowKey="referrerId"
              size="small"
              pagination={false}
              dataSource={charts?.starRanking ?? []}
              columns={[
                {
                  title: '名次', key: 'rank', width: 70,
                  render: (_: unknown, __: unknown, idx: number) => (
                    <Tag color={idx < 3 ? 'gold' : 'default'}>{idx + 1}</Tag>
                  ),
                },
                { title: '推荐人', dataIndex: 'name', key: 'name' },
                { title: '线索数', dataIndex: 'leadCount', key: 'leadCount', width: 90 },
              ]}
              locale={{ emptyText: '暂无数据' }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="业务规则速览（v2）" style={{ borderRadius: 12 }}>
            <Title level={5} style={{ marginTop: 0, fontSize: 14 }}>关键规则</Title>
            <Paragraph type="secondary" style={{ marginBottom: 4 }}>
              · 归因保护期可在「系统配置」调整（默认 90 天），新线索快照当时配置；到期自动释放。
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 4 }}>
              · 派单按小时级值班班次执行；超时未跟进线索释放入公海（每小时任务执行）。
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 4 }}>
              · 新客校验与推荐身份申请统一在「审核中心」审批；注册免审，推荐操作需申请身份。
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              · 奖励默认 = 首月租金，财务经理可调整（强制原因留痕）；发放须两押一租到账确认。
            </Paragraph>
          </Card>
        </Col>
      </Row>
    </Spin>
  )
}

export default DashboardPage
